import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar, { type SortKey } from "./components/FilterBar";

const PAGE_SIZE = 9;

function decadeOf(year: number) {
  if (year >= 2020) return "2020年代";
  if (year >= 2010) return "2010年代";
  return "2000年代";
}

export default function Category() {
  const [searchParams] = useSearchParams();
  const [genre, setGenre] = useState(searchParams.get("genre") || "");
  const [region, setRegion] = useState("");
  const [decade, setDecade] = useState("");
  const [sort, setSort] = useState<SortKey>("popular");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const g = searchParams.get("genre");
    if (g) setGenre(g);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, decade, sort]);

  const filtered = useMemo(() => {
    const list = movies.filter(
      (m) =>
        (!genre || m.genres.includes(genre)) &&
        (!region || m.region === region) &&
        (!decade || decadeOf(m.year) === decade)
    );
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.year - a.year);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.rating * 10 + b.year - (a.rating * 10 + a.year));
    return sorted;
  }, [genre, region, decade, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const clearAll = () => {
    setGenre("");
    setRegion("");
    setDecade("");
    setSort("popular");
  };

  return (
    <div className="bg-background-950 pt-24 md:pt-28 pb-16 md:pb-20">
      <div className="mx-auto max-w-site px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-foreground-500 mb-5 flex items-center gap-2">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">电影分类</span>
        </nav>

        <header className="mb-8 md:mb-10">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50">
            相关电影列表
          </h1>
          <p className="mt-3 text-sm text-foreground-400 max-w-2xl leading-relaxed">
            浏览与《交换朋友的妻子》题材相关的影视作品，可按影片类型、上映年代与制片地区筛选，快速找到想看的内容。
          </p>
        </header>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          {/* Filters */}
          <aside className="w-full lg:w-[260px] shrink-0">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-100 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line"></i> 筛选与排序
              </span>
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
            <div
              className={`mt-4 lg:mt-0 rounded-lg bg-background-900/70 border border-background-800 p-4 md:p-5 ${
                filtersOpen ? "block" : "hidden lg:block"
              }`}
            >
              <FilterBar
                genre={genre}
                region={region}
                decade={decade}
                sort={sort}
                onGenre={setGenre}
                onRegion={setRegion}
                onDecade={setDecade}
                onSort={setSort}
                onClear={clearAll}
              />
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-foreground-400">
                共找到 <span className="text-accent-400 font-label">{filtered.length}</span> 部影片
              </p>
              {(genre || region || decade) && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-close-circle-line"></i> 清除筛选
                </button>
              )}
            </div>

            {pageItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-700 p-12 text-center">
                <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-900 text-3xl text-foreground-500">
                  <i className="ri-movie-line"></i>
                </span>
                <p className="mt-4 text-foreground-300">没有找到符合条件的影片</p>
                <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或清除后重新浏览。</p>
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  清除筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                  {pageItems.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-md bg-background-900 border border-background-800 text-foreground-300 hover:border-primary-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i + 1)}
                        className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-label transition-colors cursor-pointer ${
                          page === i + 1
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-900 border border-background-800 text-foreground-300 hover:border-primary-700"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-md bg-background-900 border border-background-800 text-foreground-300 hover:border-primary-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}